// src/hooks/useAgentWebSocket.js
import { useEffect, useRef, useCallback } from 'react';
import { getAgentWS, destroyAgentWS, WS_STATES } from '../utils/ws';
import { isAuthenticated } from '../utils/api';

export function useAgentWebSocket({
    onEvent,
    onSubscribed,
    onError,
    onStateChange,
    onDisconnect,
    onHubDelivered,
    enabled = true,
}) {
    const callbacksRef = useRef({ onEvent, onSubscribed, onError, onStateChange, onDisconnect, onHubDelivered });
    callbacksRef.current = { onEvent, onSubscribed, onError, onStateChange, onDisconnect, onHubDelivered };

    useEffect(() => {
        if (!enabled || !isAuthenticated()) return;

        const ws = getAgentWS();

        ws.onStateChange = (state) => {
            callbacksRef.current.onStateChange?.(state);
        };

        ws.onAuthOk = () => {};

        ws.onSubscribed = (msg) => {
            callbacksRef.current.onSubscribed?.(msg);
        };

        ws.onEvent = (event) => {
            callbacksRef.current.onEvent?.(event);
        };

        ws.onError = (err) => {
            callbacksRef.current.onError?.(err);
        };

        ws.onDisconnect = (info) => {
            callbacksRef.current.onDisconnect?.(info);
        };

        ws.onHubDelivered = (msg) => {
            callbacksRef.current.onHubDelivered?.(msg);
        };

        ws.onControlMessage = (msg) => {
            if (msg.type === 'state_reset') {
                callbacksRef.current.onError?.({ code: 'STATE_RESET', message: msg.reason, session_id: msg.session_id });
            }
        };

        ws.connect();

        callbacksRef.current.onStateChange?.(ws.state);

        return () => {
            ws.onStateChange = null;
            ws.onAuthOk = null;
            ws.onSubscribed = null;
            ws.onEvent = null;
            ws.onError = null;
            ws.onDisconnect = null;
            ws.onHubDelivered = null;
            ws.onControlMessage = null;
        };
    }, [enabled]);

    const chat = useCallback((params) => {
        getAgentWS().chat(params);
    }, []);

    const subscribe = useCallback((sessionId, lastEventId) => {
        getAgentWS().subscribe(sessionId, lastEventId);
    }, []);

    const stop = useCallback((sessionId) => {
        getAgentWS().stop(sessionId);
    }, []);

    const pushMessage = useCallback((sessionId, message) => {
        getAgentWS().pushMessage(sessionId, message);
    }, []);

    const sendApproval = useCallback((params) => {
        getAgentWS().sendApproval(params);
    }, []);

    const send = useCallback((data) => {
        getAgentWS().send(data);
    }, []);

    const sendBinary = useCallback((data) => {
        getAgentWS().sendBinary(data);
    }, []);

    const voiceAgentStart = useCallback((params) => {
        getAgentWS().voiceAgentStart(params);
    }, []);

    const voiceAgentStop = useCallback(() => {
        getAgentWS().voiceAgentStop();
    }, []);

    const voiceAgentEndTurn = useCallback(() => {
        getAgentWS().voiceAgentEndTurn();
    }, []);

    const voiceAgentInterrupt = useCallback(() => {
        getAgentWS().voiceAgentInterrupt();
    }, []);

    const sttStart = useCallback((params) => {
        getAgentWS().sttStart(params);
    }, []);

    const sttStop = useCallback(() => {
        getAgentWS().sttStop();
    }, []);

    const disconnect = useCallback(() => {
        destroyAgentWS();
    }, []);

    const getState = useCallback(() => {
        return getAgentWS().state;
    }, []);

    const isReady = useCallback(() => {
        return getAgentWS().isConnected;
    }, []);

    return {
        chat,
        subscribe,
        stop,
        pushMessage,
        send,
        sendBinary,
        sendApproval,
        voiceAgentStart,
        voiceAgentStop,
        voiceAgentEndTurn,
        voiceAgentInterrupt,
        sttStart,
        sttStop,
        disconnect,
        getState,
        isReady,
    };
}
